import { observable, action, computed } from 'mobx'
import { configure } from "mobx";
import { RequestState } from 'src/models/RequestState';
import { TrendItem, buildTrendItem, sortTrend } from 'src/models/TrendItem';
import { ChartItem } from 'src/models/ChartItem';
import { PublicationListItem } from 'src/models/PublicationListItem';
import stopword from 'stopword'
import { Author } from 'src/models/CoAuthor';
import { ChartStore } from './ChartStore';
import {AuthorPublicationCombined, SourceTrendAggregated, SubjectAreasAggregated, CoAuthorsAggregated} from "src/models/AuthorPublicationsCombined"
import { Api } from 'src/core/api';
import axios from "axios";
import { JsonConvert } from 'json2typescript';
import { AwardByFundingAgencyItem } from 'src/models/AwardByFundingAgencyItem';
import { AwardByDepartmentItem } from 'src/models/AwardByDepartmentItem';
import { AwardTrendItem } from 'src/models/AwardTrendItem';
import { ProposalTrendItem } from 'src/models/ProposalTrendItem';
import { ProposalByFundingAgencyItem } from 'src/models/ProposalByFundingAgencyItem';
import { ProposalByDepartmentItem } from 'src/models/ProposalByDepartmentItem';
import { AwardListItem } from 'src/models/AwardListItem';
import { start } from 'repl';
import { ProposalListItem } from 'src/models/ProposalListItem';
import { AbbreviationItem } from 'src/models/AbbreviationItem';

configure({
    enforceActions: "always"
});

export enum TabOptions {
    PROPOSALS,
    AWARDS,
    EXPENDITURES,
    COMBINED,
    PUBLICATIONS
}

export class FacultyPageStore {

    @observable public requestState: RequestState;
    @observable public code: number;
    @observable public error: string;
    @observable public selectedTab: TabOptions;
    @observable public authorPageData: AuthorPageData;

    constructor() {
        this.init();
    }

    public init() {
        this.requestState = RequestState.NOT_ASKED;
        this.code = 0;
        this.error = "";
        this.selectedTab = TabOptions.AWARDS;
        this.authorPageData = new AuthorPageData();
    }

    

    @action
    public setSelectedTab(selectedTab: number) {
        this.selectedTab = selectedTab;
    }

    @action
    public setAuthorPageData(authorPageData: AuthorPageData) {
        this.authorPageData = authorPageData;
        this.requestState = RequestState.SUCCESS;
    }

    

}

export class AuthorPageData {
    @observable public firstName: string;
    @observable public lastName: string;
    @observable public affiliation: string;
    @observable public authorID: string;
    @observable public publicationData: PublicationData;
    @observable public expenditureData: ExpenditureData;
    @observable public awardData: AwardData;
    @observable public proposalData: ProposalData;
    @observable public selectedYear: number;
    @observable public requestState: RequestState;
    @observable public filteredPublicationData: PublicationData;
    @observable public filterRequestState: RequestState;
    @observable public error: string;
    @observable public startYear: number;
    @observable public endYear: number;
    
    constructor() {
        this.init();
    }

    @action
    public init(){
        this.firstName = ""
        this.lastName = ""
        this.affiliation = ""
        this.authorID = ""
        this.publicationData = new PublicationData();
        this.expenditureData = new ExpenditureData();
        this.awardData = new AwardData();
        this.proposalData = new ProposalData();
        this.selectedYear = 0;
        this.requestState = RequestState.NOT_ASKED;
        this.error = "";
        this.filteredPublicationData = new PublicationData();
        this.startYear = new Date().getFullYear() - 5;
        this.endYear = new Date().getFullYear();
        this.filterRequestState = RequestState.NOT_ASKED;
    }

    @action
    public setRequestState(requestState: RequestState) {
        this.requestState = requestState;
    }


    @action
    public fromResponse(data: AuthorPublicationCombined) {
        if(this.selectedYear === 0){
            this.firstName = data.basic_details.first_name,
            this.lastName = data.basic_details.last_name,
            //this needs to be a dict
            this.affiliation = data.basic_details.affiliation.top_level,
            this.authorID = data.basic_details.author_id
            this.publicationData.fromResponse(data)
        }else{
            this.filteredPublicationData.fromResponse(data)
        }
    }

    @action
    public async setSelectedYear(year: number) {
        this.selectedYear = year;
    }

    @action 
    public setFilterRequestState(state: RequestState) {
        this.filterRequestState = state;
    }
    
    @action
    public clearSelectedYear() {
        this.selectedYear = 0;
    }

    @action
    public setError(error: string) {
        this.requestState = RequestState.ERROR;
        this.error = error;
    }

    public getStartYear() {
        return this.startYear;
    }

    @action
    public setRange(startYear: number, endYear: number) {
        this.startYear = startYear;
        this.endYear = endYear;
    }
   
    
}

export class PublicationData {
    
    @observable public h_index: string;
    @observable public document_count: string;
    @observable public citations_count: string;
    @observable public trend: TrendItem[];
    @observable public publications_by_journal: ChartItem[];
    @observable public coauthors: ChartItem[];
    @observable public selected_co_investigators_tab: number;
    @observable public publications_by_subject_area: ChartItem[];
    @observable public publications_detail: PublicationListItem[]

    constructor() {
        this.init()
    }

    public init() {
        this.h_index = "",
        this.document_count = "",
        this.citations_count = "",
        this.trend = [],
        this.publications_by_journal = [],
        this.coauthors = [],
        this.selected_co_investigators_tab = 0,
        this.publications_by_subject_area = [],
        this.publications_detail = []
    }

    @action
    public fromResponse(data: AuthorPublicationCombined) {
        this.h_index = data.basic_details.h_index,
        this.document_count = data.basic_details.document_count,
        this.citations_count = data.basic_details.cited_by,
        this.trend = data.documents_trend.map(buildTrendItem).sort(sortTrend),
        //using the aggregated one, other one is also included in the call
        this.publications_by_journal = buildJournalItems(data.source_trend_aggregated),
        //using the aggregated one
        this.coauthors = data.co_authors_aggregated.map(buildCoauthorChartItems).sort(sortItem),
        //using the aggregated one
        this.publications_by_subject_area = buildPublicationSubjectarea(data.subject_areas_aggregated),
        this.publications_detail = data.publications;
    }
}

export class ExpenditureData {
    
    @observable public expenditures_over_time: ChartStore<TrendItem>
    @observable public expenditures_by_project: ChartStore<ChartItem>
    @observable public expeditures_by_department: ChartStore<ChartItem>

    constructor(){
        this.init()
    }

    private init(){
        this.expenditures_over_time = new ChartStore()
        this.expeditures_by_department = new ChartStore()
        this.expenditures_by_project = new ChartStore()
    }
}

export class AwardData {
    @observable public awards_over_time: ChartStore<AwardTrendItem>
    @observable public awards_by_fundingagency: ChartStore<AwardByFundingAgencyItem>
    @observable public awards_by_department: ChartStore<AwardByDepartmentItem>
    @observable public awards_list: ChartStore<AwardListItem>

    constructor(){
        this.init()
    }

    private init(){
        this.awards_over_time = new ChartStore()
        this.awards_by_department = new ChartStore()
        this.awards_by_fundingagency = new ChartStore()
        this.awards_list = new ChartStore();
    }
}

export class ProposalData {
    @observable public proposals_over_time: ChartStore<ProposalTrendItem>
    @observable public proposals_by_fundingagency: ChartStore<ProposalByFundingAgencyItem>
    @observable public proposals_by_department: ChartStore<ProposalByDepartmentItem>
    @observable public proposals_list: ChartStore<ProposalListItem>

    constructor(){
        this.init()
    }

    private init(){
        this.proposals_over_time = new ChartStore()
        this.proposals_by_department = new ChartStore()
        this.proposals_by_fundingagency = new ChartStore()
        this.proposals_list = new ChartStore();
    }
}


function buildPublicationSubjectarea(pub_items_object: SubjectAreasAggregated[]) {
    const pub_list: ChartItem[] = [];
    pub_items_object.forEach((dic: SubjectAreasAggregated) => {
        const abbr_item = new AbbreviationItem();
        abbr_item.name = dic.area_name;
        abbr_item.name_abbr = dic.area_name_abbr;
        const name_str = JSON.stringify(abbr_item);
        const count = dic.count;
        const item = new ChartItem(name_str, count, dic)
        pub_list.push(item)
    });
    pub_list.sort(sortItem);
    return pub_list;
}

function buildJournalItems(pub_items_object: SourceTrendAggregated[]) {
    //const keys = Object.keys(pub_items_object);
    const pub_list: ChartItem[] = []
    pub_items_object.forEach((dic: SourceTrendAggregated) => {
        const abbr_item = new AbbreviationItem();
        abbr_item.name = dic.source_name;
        abbr_item.name_abbr = dic.source_name_abbr;
        const data = JSON.stringify(dic);
        const name_str = JSON.stringify(abbr_item);
        const item = new ChartItem(name_str,+dic.source_count,data)
        pub_list.push(item)
    });
    //for(const key of keys){
    //      const name_long = key;
    //    const name_long_list = stopword.removeStopwords(name_long.split(" "));
    //  let name_short="";
    // for(const name of name_long_list){
    //     name_short = name_short+name[0];
    //  }
    //  const item = new ChartItem(name_short,pub_items_object[key],name_long)        
    //  pub_list.push(item)
    // }
    pub_list.sort(sortItem);
    return pub_list;
}

function buildCoauthorChartItems(author: CoAuthorsAggregated, _index: number) {
    const first_name = author.first_name;
    const last_name = author.last_name;
    let name = '';
    name = first_name + '. ' + last_name;
    const count = author.count;
    return new ChartItem(name, count, author);
}

function buildCoAuthorItem(data: any, index: number) {
    return {
        ...new Author(),
        name: data.first_name + '. ' + data.last_name,
        id: data.author_id,
        affiliation_id: data.affiliation_id
    }
}

export function sortItem(item1: ChartItem, item2: ChartItem) {
    return item2.amount - item1.amount;
}




// WEBPACK FOOTER //
// ./src/mobx/FacultyPageStore.ts